import { useCallback, useEffect, useRef, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import useFocus from '@/hooks/useFocus';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import Button from '@/trio-ui/Button';
import * as Styles from './SearchDropdownButton.styles';
import Loading from '../general/Loading';

export type Props<T> = {
  className?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  items: T[];
  onResultClick?: (item: T) => void;
  showInput?: boolean;
  onShowInputChange?: (value: boolean) => void;
  loading?: boolean;
  renderItem: (item: T) => JSX.Element;
  keyExtractor?: (item: T) => string;
  buttonLabel?: string;
  itemButtonLabel?: string;
};

const inputId = 'search-dropdown-button-input';
const dropdownId = 'search-dropdown-button-dropdown';

function SearchDropdownButtonComponent<T>({
  searchValue = '',
  onSearchChange,
  items,
  onResultClick,
  showInput: externalShowInput,
  onShowInputChange,
  loading = false,
  renderItem,
  keyExtractor,
  buttonLabel = 'Add',
  itemButtonLabel = 'Add',
  ...props
}: Props<T>): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const focus = useFocus(inputRef);
  const [internalShowInput, setShowInput] = useState<boolean>(false);
  const [isDropdownVisible, setDropdownVisible] = useState<boolean>(true);

  const showInput = externalShowInput ?? internalShowInput;

  useEffect(() => {
    if (focus) setDropdownVisible(true);
  }, [focus]);

  const hideDropdown = useCallback((event: any) => {
    const eventPath = event.composedPath() ?? [];
    const componentIDs = [inputId, dropdownId];
    const checkPathId = (path): boolean => componentIDs.includes(path.id);
    const hasClickedInsideComponent = eventPath.some(checkPathId);

    if (!hasClickedInsideComponent) {
      setDropdownVisible(false);
    }
  }, []);

  useOnClickOutside(containerRef, hideDropdown);

  const handleItemClick = (item: T): void => {
    onResultClick?.(item);
    onSearchChange?.('');
  };

  const handleButtonClick = (): void => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    const value = Boolean(focus || searchValue);

    if (value !== showInput) {
      setShowInput(value);
      onShowInputChange?.(value);
    }
  }, [focus, searchValue, setShowInput, showInput, onShowInputChange]);

  const handleCancelClick = (): void => {
    onSearchChange?.('');
    inputRef.current?.blur();
  };

  return (
    <Styles.Box {...props}>
      <Styles.Input
        id={inputId}
        className={!showInput ? 'hidden' : undefined}
        value={searchValue}
        onChange={(e) => onSearchChange?.(e.target.value)}
        ref={inputRef}
        isLabelAboveField
      />
      {showInput && searchValue && (
        <Styles.CancelButton onClick={handleCancelClick}>
          <CancelIcon />
        </Styles.CancelButton>
      )}
      {!showInput && (
        <Styles.ActivateButton
          variantColor="blue"
          variantStyle="outline"
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </Styles.ActivateButton>
      )}
      {searchValue && isDropdownVisible && (
        <Styles.Dropdown id={dropdownId} ref={containerRef}>
          {items.map((item, i) => (
            <Styles.ListItem key={keyExtractor ? keyExtractor(item) : i}>
              {renderItem(item)}
              <Button
                variantColor="blue"
                variantStyle="filled"
                onClick={() => handleItemClick(item)}
              >
                {itemButtonLabel}
              </Button>
            </Styles.ListItem>
          ))}
          {loading && (
            <Styles.LoadingContainer>
              <Loading />
            </Styles.LoadingContainer>
          )}
        </Styles.Dropdown>
      )}
    </Styles.Box>
  );
}

export default SearchDropdownButtonComponent;
